import { useMemo, useRef, useState } from 'react';
import { formatHitFactor, formatMs } from '@flm9/i18n/labels';
import { ipscPoints, roundHitFactor, type ScoringMode } from '@flm9/scoring';
import { queueShot, correctQueuedShot, type CachedShot } from '@flm9/sync';
import { track } from '@flm9/telemetry-client';
import { wakeRefereeSync } from '../lib/sync';
import {
  HitIndicator,
  type HitZone,
  type PendingDetection,
  type PenaltyEntry,
  type ShotPosition,
  type TargetZone,
} from '@flm9/stream-client';
import { useTimerRun } from '../lib/timer/provider';
import { ShotUndoToast } from './ShotUndoToast';
import { formatPenaltyPoints } from '../lib/penalties';

interface Props {
  assignmentId: string;
  shotCount: number;
  shooterLabel: string;
  shots: CachedShot[];
  onShot: () => void;
  /**
   * Schema-defined zones (A/C/D/M/NS for IPSC; admin-tunable values).
   * Drives the hit indicator's per-zone colour mapping. The keypad
   * still uses the canonical IPSC layout (5 zones) — switching IPSC
   * to a different layout would be a separate UX change.
   */
  schemaZones: HitZone[];
  /**
   * The stage's scoring-schema config (power factor / custom zone points).
   * Zone points come from it via `ipscPoints` — same source as the server
   * (`buildScoringConfig`) and the native keypad; invalid → IPSC minor.
   */
  schemaConfig?: unknown;
  /** Geometric zone definitions for shape-faithful rendering. */
  targetZones?:
    | Array<{
        zoneCode: string;
        ordering: number;
        shape: 'circle' | 'rect' | 'polygon';
        geometry: unknown;
        displayColor: string | null;
      }>
    | undefined;
  shotPositions?: ShotPosition[] | undefined;
  pendingDetections?: PendingDetection[] | undefined;
  penalties?: PenaltyEntry[] | undefined;
  onConfirmDetection?: ((detectionId: string) => void) | undefined;
  onDismissDetection?: ((detectionId: string) => void) | undefined;
  /** When true, suppress the inline hit indicator — the parent renders
   *  it as a floating PiP overlay instead, so showing it inline would
   *  be a duplicate. */
  hideHitIndicator?: boolean;
  /** Current PiP-mode state, used to label the toggle button. When
   *  `onToggleHitPip` is provided, a small always-visible toggle is
   *  rendered in the header so the referee can pop the indicator out
   *  before the first shot lands. */
  hitPipActive?: boolean;
  onToggleHitPip?: () => void;
  /** Camera PiP toggle — sibling pill next to the hit-PiP toggle. */
  streamPipActive?: boolean;
  onToggleStreamPip?: () => void;
  /** Idempotency keys of shots still in the outbox (REF-9: amber dot). */
  unsentKeys?: ReadonlySet<string>;
  /**
   * Sum of the shooter's penalties incl. queued ones (`penaltyTotal`, ≤ 0).
   * Added to the points shown here (and the HF preview).
   */
  penaltyPoints?: number;
  /**
   * Round timing from the TimingPad (REF-8, the only time entry): the HF
   * preview and the first / last shot payload use it.
   */
  firstShotTimeMs?: number | null;
  elapsedTimeMs?: number | null;
  /**
   * The SCORING MODE decides whether there is a hit factor (engine rule,
   * `roundHitFactor`): speed only — not the schema kind.
   */
  scoringMode: ScoringMode;
  /** A DQ'd round has no hit factor (server: leaderboard.ts sets it null). */
  disqualified?: boolean;
}

const ZONES: Array<{ code: 'A' | 'C' | 'D' | 'M' | 'NS'; label: string }> = [
  { code: 'A', label: 'Alpha' },
  { code: 'C', label: 'Charlie' },
  { code: 'D', label: 'Delta' },
  { code: 'M', label: 'Miss' },
  { code: 'NS', label: 'No-Shoot' },
];

export function IpscKeypad({
  assignmentId,
  shotCount,
  shooterLabel,
  shots,
  onShot,
  schemaZones,
  schemaConfig,
  targetZones,
  shotPositions,
  pendingDetections,
  penalties,
  onConfirmDetection,
  onDismissDetection,
  hideHitIndicator,
  hitPipActive,
  onToggleHitPip,
  streamPipActive,
  onToggleStreamPip,
  unsentKeys,
  penaltyPoints = 0,
  firstShotTimeMs = null,
  elapsedTimeMs = null,
  scoringMode,
  disqualified = false,
}: Props) {
  // Zone points from the schema config (major / minor / custom).
  const zonePoints = useMemo(() => ipscPoints(schemaConfig).points, [schemaConfig]);
  // Shot being corrected in the edit sheet (UX review REF-1).
  const [editing, setEditing] = useState<CachedShot | null>(null);
  const [busy, setBusy] = useState(false);
  // Last deleted shot, offered for undo (REF-21).
  const [deleted, setDeleted] = useState<CachedShot | null>(null);
  // Scoped to this shooter: a previous shooter's stopped run must not
  // lend its shot times to this one.
  const {
    source: timerSource,
    status: timerStatus,
    isConnected: timerConnected,
    ownsRun: timerRunIsOurs,
  } = useTimerRun(assignmentId);
  // BLE-detected shots that haven't been zoned yet — rendered as
  // ghost cells in the bottom shot list so the referee can see how
  // many hits the timer caught that OpenCV / they themselves haven't
  // turned into a zoned shot. Capped at shotCount because anything
  // past that overshoots the stage and won't be recorded anyway.
  const pendingBleShots =
    timerConnected && timerStatus.shots.length > shots.length
      ? timerStatus.shots.slice(shots.length, shotCount)
      : [];

  const recording = useRef(false);
  async function recordShot(zone: 'A' | 'C' | 'D' | 'M' | 'NS') {
    if (recording.current) return;
    recording.current = true;
    // Flight recorder: what the referee entered → which shot got queued
    // (the upload itself is tracked by the sync drainer).
    const intent = track('Schuss erfassen', { assignmentId, zone });
    try {
      const queued = await queueShot({
        assignmentId,
        shotCount,
        fields: (ord) => {
          const bleShot =
            timerConnected && timerRunIsOurs
              ? timerSource.status().shots.find((s) => s.ord === ord)
              : undefined;
          return {
            valueNumeric: null,
            zoneCode: zone,
            firstShotTimeMs: bleShot?.tFromStartMs ?? (ord === 1 ? firstShotTimeMs : null),
            elapsedTimeMs: ord === shotCount ? (bleShot?.tFromStartMs ?? elapsedTimeMs) : null,
            ...(bleShot ? { source: 'ble_timer' as const } : {}),
          };
        },
      });
      if (queued)
        intent.ok({ ord: queued.ord, clientKey: queued.clientIdempotencyKey, queued: true });
      else intent.fail('not queued (stage already full)');
      wakeRefereeSync();
      onShot();
    } catch (err) {
      intent.fail(err);
      console.error('recordShot failed', err);
      alert('Schuss konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      recording.current = false;
    }
  }

  async function applyEdit(zone: 'A' | 'C' | 'D' | 'M' | 'NS') {
    if (!editing) return;
    setBusy(true);
    try {
      await correctQueuedShot(editing.roundAssignmentId, editing.clientIdempotencyKey, {
        zoneCode: zone,
      });
      wakeRefereeSync();
      onShot();
      setEditing(null);
    } catch (err) {
      console.error('updateShot failed', err);
      alert('Korrektur fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  }

  async function applyDelete() {
    if (!editing) return;
    setBusy(true);
    try {
      await correctQueuedShot(editing.roundAssignmentId, editing.clientIdempotencyKey, null);
      wakeRefereeSync();
      onShot();
      setDeleted(editing);
      setEditing(null);
    } catch (err) {
      console.error('removeShot failed', err);
      alert('Löschen fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  }

  const sum =
    shots.reduce((acc, s) => {
      const z = ZONES.find((x) => x.code === s.zoneCode);
      return acc + (z ? zonePoints[z.code] : 0);
    }, 0) + penaltyPoints;
  const hf = disqualified ? null : roundHitFactor(scoringMode, sum, elapsedTimeMs);

  return (
    <section>
      <div className="mb-4 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">am Schießstand</p>
          <h2 className="truncate text-3xl font-extrabold leading-tight sm:text-4xl">
            {shooterLabel}
          </h2>
          <p className="mt-1 text-sm text-zinc-400">
            {shots.length} / {shotCount} Treffer
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Punkte</p>
          <div className="text-4xl font-bold tabular-nums">{sum}</div>
          {penaltyPoints !== 0 && (
            <div className="text-xs tabular-nums text-orange-300">
              inkl. Strafen {formatPenaltyPoints(penaltyPoints)}
            </div>
          )}
          {hf != null && (
            <div className="text-xs text-emerald-400 tabular-nums">HF {formatHitFactor(hf)}</div>
          )}
        </div>
      </div>

      {(onToggleHitPip || onToggleStreamPip) && (
        <div className="mb-3 flex flex-wrap justify-end gap-2">
          {onToggleHitPip && (
            <button
              onClick={onToggleHitPip}
              title={
                hitPipActive
                  ? 'Trefferanzeige inline anzeigen'
                  : 'Trefferanzeige als Bild-im-Bild ausklappen'
              }
              aria-pressed={hitPipActive ? 'true' : 'false'}
              className={`min-h-12 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                hitPipActive
                  ? 'border-emerald-500 bg-emerald-700 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              {hitPipActive ? '🪟 PiP an' : '🪟 Trefferanzeige als PiP'}
            </button>
          )}
          {onToggleStreamPip && (
            <button
              onClick={onToggleStreamPip}
              title={
                streamPipActive
                  ? 'Kamera-PiP schließen'
                  : 'Live-Kamera als Bild-im-Bild — weckt die CV-Erkennung'
              }
              aria-pressed={streamPipActive ? 'true' : 'false'}
              className={`min-h-12 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                streamPipActive
                  ? 'border-amber-400 bg-amber-600 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              {streamPipActive ? '📹 Kamera an' : '📹 Kamera als PiP'}
            </button>
          )}
        </div>
      )}

      <div className="mb-6 grid grid-cols-3 gap-2">
        {ZONES.map((z) => (
          <button
            key={z.code}
            onClick={() => void recordShot(z.code)}
            disabled={shots.length >= shotCount}
            className="rounded-lg border-2 border-zinc-700 px-4 py-6 text-2xl font-bold hover:border-emerald-500 active:bg-emerald-700 disabled:opacity-30"
          >
            <div>{z.code}</div>
            <div className="text-xs text-zinc-500">
              {z.label} · {zonePoints[z.code] >= 0 ? '+' : ''}
              {zonePoints[z.code]}
            </div>
          </button>
        ))}
      </div>

      {shots.length > 0 && !hideHitIndicator && (
        <div className="mb-4">
          <HitIndicator
            shots={shots}
            shotCount={shotCount}
            schemaKind="hit_factor"
            zones={schemaZones}
            targetZones={targetZones as TargetZone[] | undefined}
            shotPositions={shotPositions}
            pendingDetections={pendingDetections}
            penalties={penalties}
            onConfirmDetection={onConfirmDetection}
            onDismissDetection={onDismissDetection}
          />
        </div>
      )}

      <p className="mb-1 text-xs text-zinc-500">
        Tipp: Treffer antippen, um die Zone zu korrigieren oder zu löschen.
        {pendingBleShots.length > 0 && (
          <>
            {' '}
            <span className="text-amber-400">
              {pendingBleShots.length} vom Timer erkannt — Zone oben wählen.
            </span>
          </>
        )}
      </p>
      <ul className="grid grid-cols-6 gap-1 text-center text-sm">
        {shots.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => setEditing(s)}
              className="relative block min-h-12 w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 hover:border-emerald-500 active:bg-emerald-900"
            >
              <span className="text-xs text-zinc-500">#{s.ord}</span>
              <br />
              {s.zoneCode}
              {unsentKeys?.has(s.clientIdempotencyKey) && (
                <>
                  <span
                    aria-hidden
                    className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-400"
                  />
                  <span className="sr-only"> noch nicht gesendet</span>
                </>
              )}
            </button>
          </li>
        ))}
        {pendingBleShots.map((bleShot) => (
          <li
            key={`pending-${bleShot.ord}`}
            className="rounded border-2 border-dashed border-amber-500/60 bg-amber-950/30 px-2 py-1 text-amber-200"
            title="Vom Timer erkannt — Zone oben wählen"
          >
            <span className="text-xs text-amber-400/80">#{bleShot.ord}</span>
            <br />
            <span className="tabular-nums">{formatMs(bleShot.tFromStartMs)}</span>
          </li>
        ))}
      </ul>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center"
          onClick={() => !busy && setEditing(null)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-zinc-900 p-4 sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-bold">Treffer #{editing.ord} korrigieren</h3>
            <p className="mb-3 text-xs text-zinc-500">
              Bisherige Zone: <span className="font-bold">{editing.zoneCode}</span>
            </p>
            <div className="mb-3 grid grid-cols-3 gap-2">
              {ZONES.map((z) => (
                <button
                  key={z.code}
                  onClick={() => void applyEdit(z.code)}
                  disabled={busy}
                  className={`rounded-lg border-2 px-4 py-4 text-2xl font-bold hover:border-emerald-500 disabled:opacity-30 ${
                    z.code === editing.zoneCode
                      ? 'border-emerald-500 bg-emerald-900/30'
                      : 'border-zinc-700'
                  }`}
                >
                  <div>{z.code}</div>
                  <div className="text-xs text-zinc-500">{z.label}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => void applyDelete()}
                disabled={busy}
                className="min-h-12 rounded border border-red-700 px-4 py-2 text-sm text-red-300 hover:bg-red-900/40 disabled:opacity-30"
              >
                Löschen
              </button>
              <button
                onClick={() => setEditing(null)}
                disabled={busy}
                className="min-h-12 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-300 disabled:opacity-30"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      <ShotUndoToast
        shot={deleted}
        shotCount={shotCount}
        onDone={() => setDeleted(null)}
        onRestored={onShot}
      />
    </section>
  );
}
